import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/sl'
import relativeTime from 'dayjs/plugin/relativeTime'

import { INTL_LOCALE, Locale } from '@/enums/locale'
import i18n from '@/libs/i18n'
import { isDay } from '@/utils/calendar'

dayjs.extend(relativeTime)

/*
 * The configured instance (vue-spec libraries.md): everything that formats an instant takes
 * it from here, so there is one set of plugins and one set of locales — Slovenian and English.
 */
export { dayjs }

const locale = (): Locale => {
    const value = (i18n.global.locale as unknown as { value: string }).value

    return value === Locale.En ? value : Locale.Sl
}

/** The BCP 47 tag the current language hands to Intl. */
export const intlLocale = (): string => INTL_LOCALE[locale()]

/* HOW A DATE IS WRITTEN, per language: Slovenian "23. 9. 2026", English "23 Sep 2026". */
const PATTERNS: Record<Locale, { day: string, dayMonth: string, dateTime: string, dateTimeSeconds: string }> = {
    [Locale.Sl]: { day: 'D. M. YYYY', dayMonth: 'D. M.', dateTime: 'D. M. YYYY HH:mm', dateTimeSeconds: 'D. M. HH:mm:ss' },
    [Locale.En]: { day: 'D MMM YYYY', dayMonth: 'D MMM', dateTime: 'D MMM YYYY HH:mm', dateTimeSeconds: 'D MMM HH:mm:ss' },
}

const patterns = () => PATTERNS[locale()]

/** A calendar day ("YYYY-MM-DD", no time zone): "1. 10. 2026". */
export const formatDay = (value?: string | null): string => {
    if (!isDay(value)) return ''

    return dayjs(value).locale(locale()).format(patterns().day)
}

/** An instant (RFC 3339) in the reader's zone: "23. 9. 2026 14:05". */
export const formatDateTime = (value?: string | null): string => {
    if (!value) return ''

    return dayjs(value).locale(locale()).format(patterns().dateTime)
}

/** An instant to the second, without the year (a log line): "23. 9. 14:05:07". */
export const formatDateTimeSeconds = (value?: string | null): string => {
    if (!value) return ''

    return dayjs(value).locale(locale()).format(patterns().dateTimeSeconds)
}

/** A calendar day ("YYYY-MM-DD") without the year: "1. 10.", "1 Oct". */
export const formatDayMonth = (value?: string | null): string => {
    if (!isDay(value)) return ''

    return dayjs(value).locale(locale()).format(patterns().dayMonth)
}

/** The month of a calendar day ("YYYY-MM-DD"): "okt. 2026", "Oct 2026". */
export const formatMonth = (value?: string | null): string => {
    if (!isDay(value)) return ''

    return dayjs(value).locale(locale()).format('MMM YYYY')
}

/** An instant's day in the reader's zone: "23. 9. 2026". */
export const formatDate = (value?: string | null): string => {
    if (!value) return ''

    return dayjs(value).locale(locale()).format(patterns().day)
}

/*
 * An instant soon to come, as a person reads a timetable: "danes 07:00", "jutri 07:00", else
 * "3. 10. 07:00" (the reader's zone).
 */
export const formatUpcoming = (value?: string | null): string => {
    if (!value) return ''

    const at   = dayjs(value).locale(locale())
    const days = at.startOf('day').diff(dayjs().startOf('day'), 'day')
    const time = at.format('HH:mm')

    if (days === 0) return i18n.global.t('time.today_at', { time })
    if (days === 1) return i18n.global.t('time.tomorrow_at', { time })

    return `${at.format(patterns().dayMonth)} ${time}`
}

/*
 * How long ago something happened, in words: "pred 3 minutami", "3 minutes ago". Day.js carries
 * each language's phrasing, the Slovenian dual included.
 */
export const agoLabel = (value?: string | null): string => {
    if (!value) return ''

    return dayjs(value).locale(locale()).fromNow()
}

/*
 * How long since an instant, without "ago": "3 ure", "3 hours" — for "Ne dela že 3 ure". After
 * "že" Slovenian takes the accusative, which Day.js's nominative differs from only in "a minute"
 * and "an hour" ("ena minuta" → "eno minuto"): those two come from the locale (time.for_*),
 * in Day.js's own ranges (45–89 s, 45–89 min).
 */
export const durationSince = (value?: string | null): string => {
    if (!value) return ''

    const seconds = dayjs().diff(dayjs(value), 'second')

    if (seconds >= 45 && seconds < 90) return i18n.global.t('time.for_a_minute')
    if (seconds >= 45 * 60 && seconds < 90 * 60) return i18n.global.t('time.for_an_hour')

    return dayjs(value).locale(locale()).fromNow(true)
}
